/**
 * Solicitar folios al SII, en dos etapas.
 *
 * 🔴 **Pedir folios es irreversible.** Un folio autorizado queda registrado a
 * nombre del contribuyente y el SII lo cuenta como disponible, reduciendo lo que
 * autoriza después. Por eso el modal no pide nada hasta que la persona ve cuánto
 * autoriza el SII y confirma.
 *
 * ⚠️ Y por eso son **dos etapas y no un botón**: `MAX_AUTOR` no se puede estimar.
 * Se midió en 1 con 34 folios disponibles, 1 con 126 y 4 con 5 — es una política
 * del SII para ese contribuyente. Pedir a ciegas es un rechazo esperando ocurrir.
 */
import { useState } from 'react'
import { AlertTriangle, Info, Loader2 } from 'lucide-react'

import {
  useConsultarSii,
  useSolicitarFolios,
  type ConsultaSii,
  type EstadoFolios,
} from '../hooks/useConfig'
import { Modal } from '@/components/ds/Modal'

interface Props {
  documentTypeCode: number
  documentTypeLabel: string
  estado?: EstadoFolios
  onClose: () => void
}

export function SolicitarFoliosModal({
  documentTypeCode,
  documentTypeLabel,
  estado,
  onClose,
}: Props) {
  const consultar = useConsultarSii()
  const solicitar = useSolicitarFolios()
  const [consulta, setConsulta] = useState<ConsultaSii | null>(null)
  const [cantidad, setCantidad] = useState('1')
  const [enviada, setEnviada] = useState(false)

  const maximo = consulta?.max_autorizado ?? 0
  const pedido = Number.parseInt(cantidad, 10)
  const cantidadValida = Number.isFinite(pedido) && pedido >= 1 && pedido <= maximo

  const handleConsultar = () => {
    consultar.mutate(documentTypeCode, {
      onSuccess: (data) => {
        setConsulta(data)
        // Se propone el máximo, que es lo que la gente quiere casi siempre: el
        // SII ya lo topó por nosotros.
        if (data.max_autorizado) setCantidad(String(data.max_autorizado))
      },
    })
  }

  const handleSolicitar = () => {
    solicitar.mutate(
      { documentTypeCode, cantidad: pedido },
      { onSuccess: () => setEnviada(true) }
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Solicitar folios al SII"
      dismissOnScrimClick={false}
    >
      <div className="space-y-4">
        <p className="text-sm text-content-muted">{documentTypeLabel}</p>

        {enviada ? (
          <Enviada onClose={onClose} />
        ) : (
          <>
            {/* Lo que Clobi ya sabe, sin preguntarle al SII. */}
            {estado && estado.folios_sin_usar > 0 && (
              <FoliosSinUsar estado={estado} />
            )}

            {!consulta ? (
              <PasoConsultar
                cargando={consultar.isPending}
                error={consultar.error}
                onConsultar={handleConsultar}
              />
            ) : (
              <PasoConfirmar
                consulta={consulta}
                cantidad={cantidad}
                onCantidad={setCantidad}
                cantidadValida={cantidadValida}
                enviando={solicitar.isPending}
                error={solicitar.error}
                onSolicitar={handleSolicitar}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

/**
 * Los folios que ya están autorizados y sin usar.
 *
 * Se muestra **antes** de consultar porque suele ser la respuesta a «¿por qué el
 * SII me autoriza tan poco?»: son ellos los que bajan el máximo.
 */
function FoliosSinUsar({ estado }: { estado: EstadoFolios }) {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
      <div className="flex gap-2">
        <Info size={16} className="mt-0.5 shrink-0 text-warning-content-soft" />
        <div className="text-sm text-content">
          <p>
            Hay <strong>{estado.folios_sin_usar}</strong> folio(s) autorizados que
            nunca se usaron.
          </p>
          <p className="mt-1 text-content-muted">
            El SII los cuenta como disponibles y por eso autoriza menos. Anularlos
            ante el SII libera cupo.
          </p>
          {estado.tramos_sin_usar.length > 0 && (
            <p className="mt-1 font-mono text-xs text-content-muted">
              {estado.tramos_sin_usar
                .slice(0, 5)
                .map((t) => (t.desde === t.hasta ? t.desde : `${t.desde}-${t.hasta}`))
                .join(' · ')}
              {estado.tramos_sin_usar.length > 5 && ' …'}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/** Etapa 1: preguntarle al SII. No compromete nada. */
function PasoConsultar({
  cargando,
  error,
  onConsultar,
}: {
  cargando: boolean
  error: unknown
  onConsultar: () => void
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-content">
        Primero se consulta al SII cuántos folios autoriza. La consulta no solicita
        nada.
      </p>
      {error != null && <ErrorDelServidor error={error} />}
      <button
        onClick={onConsultar}
        disabled={cargando}
        className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-on-primary disabled:opacity-60"
      >
        {cargando && <Loader2 size={16} className="animate-spin" />}
        {cargando ? 'Consultando al SII…' : 'Consultar al SII'}
      </button>
      {cargando && (
        <p className="text-center text-xs text-content-muted">
          Puede tardar unos segundos: se conecta al portal del SII.
        </p>
      )}
    </div>
  )
}

/** Etapa 2: 🔴 la que compromete. */
function PasoConfirmar({
  consulta,
  cantidad,
  onCantidad,
  cantidadValida,
  enviando,
  error,
  onSolicitar,
}: {
  consulta: ConsultaSii
  cantidad: string
  onCantidad: (v: string) => void
  cantidadValida: boolean
  enviando: boolean
  error: unknown
  onSolicitar: () => void
}) {
  // 🔴 El SII no autoriza. Su mensaje va TAL CUAL: él mismo no distingue entre
  // «tienes situaciones pendientes» y «ya tienes folios de sobra», así que Clobi no
  // inventa el diagnóstico.
  if (!consulta.puede_pedir) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
          <div className="flex gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-danger-content-soft" />
            <div className="text-sm text-content">
              <p className="font-medium">El SII no autoriza folios ahora</p>
              <p className="mt-1 text-content-muted">{consulta.explicacion}</p>
            </div>
          </div>
        </div>
        {consulta.mensaje_del_sii && (
          <div className="rounded-md border border-divider bg-canvas p-3">
            <p className="mb-1 text-xs font-medium uppercase text-content-muted">
              Respuesta del SII
            </p>
            <p className="whitespace-pre-line text-xs text-content-muted">
              {consulta.mensaje_del_sii}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 rounded-md border border-divider bg-canvas p-3 text-sm">
        <div>
          <dt className="text-xs text-content-muted">El SII autoriza hasta</dt>
          <dd className="text-lg font-semibold text-content">
            {consulta.max_autorizado}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-content-muted">Folios sin usar</dt>
          <dd className="text-lg font-semibold text-content">
            {consulta.folios_disponibles ?? 0}
          </dd>
        </div>
      </dl>

      <label className="block">
        <span className="text-sm text-content">Folios a solicitar</span>
        <input
          type="number"
          min={1}
          max={consulta.max_autorizado ?? 1}
          value={cantidad}
          onChange={(e) => onCantidad(e.target.value)}
          className="mt-1 w-full rounded-md border border-divider bg-surface px-3 py-2 text-content focus:ring-2 focus:ring-focus-ring"
        />
        {!cantidadValida && (
          <span className="mt-1 block text-xs text-danger-content-soft">
            Debe ser entre 1 y {consulta.max_autorizado}, que es lo que el SII
            autoriza.
          </span>
        )}
      </label>

      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-content">
        <strong>Esto no se puede deshacer.</strong> Los folios quedan registrados a
        tu nombre en el SII, y los que no uses reducen lo que autorice la próxima
        vez.
      </div>

      {error != null && <ErrorDelServidor error={error} />}

      <button
        onClick={onSolicitar}
        disabled={!cantidadValida || enviando}
        className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-on-primary disabled:opacity-60"
      >
        {enviando && <Loader2 size={16} className="animate-spin" />}
        {enviando ? 'Solicitando…' : `Solicitar ${cantidad} folio(s)`}
      </button>
    </div>
  )
}

function Enviada({ onClose }: { onClose: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-content">
        La solicitud se envió al SII. Cuando termine, el CAF se carga solo y los
        folios quedan disponibles para emitir.
      </p>
      <p className="text-xs text-content-muted">
        Puedes cerrar esta ventana: el trámite sigue en segundo plano.
      </p>
      <button
        onClick={onClose}
        className="w-full rounded-md border border-divider px-4 py-2 text-sm text-content hover:bg-canvas"
      >
        Cerrar
      </button>
    </div>
  )
}

/**
 * El error del backend con su mensaje real.
 *
 * ⚠️ Se muestra el mensaje del servidor y no uno genérico: cada código
 * (`FOLIOS_YA_ASIGNADOS`, `REQUEST_IN_PROGRESS`, `SII_ERROR`) explica una salida
 * distinta, y «ocurrió un error» dejaría a la persona sin saber qué hacer.
 */
function ErrorDelServidor({ error }: { error: unknown }) {
  const mensaje =
    (error as { response?: { data?: { error?: { message?: string } } } })?.response
      ?.data?.error?.message ?? 'No se pudo completar la operación.'
  return (
    <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-content">
      {mensaje}
    </div>
  )
}
